import React, { ComponentProps, useRef } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_300,
  COLOR_PRIMARY_900,
  COLOR_SECONDARY_200,
  COLOR_SECONDARY_50,
  GOOGLE_PLACES_API_KEY,
} from '../../../constants';
import httpService from '../../../services/httpService';
import eventUtils from '../../../utils/eventUtils';

type AutoCompleteLocationInputProps = {
  location: string;
  setLocation: (location: { location: string; locationUrl: string | undefined }) => void;
  placeholder?: string;
} & ComponentProps<typeof GooglePlacesAutocomplete>;

export default function AutoCompleteLocationInput({
  location,
  setLocation,
  ...props
}: AutoCompleteLocationInputProps) {
  const isSelectingRef = useRef(false);

  const handleClick = (name: string | undefined, placeId: string | undefined) => {
    if (!placeId) {
      setLocation({ location: name || '', locationUrl: undefined });
      return;
    }

    const locationUrl = eventUtils.buildGoogleMapsUrlFromPlaceId(placeId, name || '');
    setLocation({ location: name || '', locationUrl });

    httpService
      .get(`/google-locations/${placeId}`)
      .then(({ data }) => {
        const apiUrl = data?.url ?? data?.result?.url;
        if (!apiUrl) return;
        setLocation({ location: name || '', locationUrl: apiUrl });
      })
      .catch(() => {
        // Backend lookup is optional; client-side place_id URL is already set.
      });
  };

  return (
    <GooglePlacesAutocomplete
      apiKey={GOOGLE_PLACES_API_KEY}
      debounce={300}
      minLengthAutocomplete={3}
      selectProps={{
        inputId: 'location-input',
        inputValue: location,
        onInputChange: (l, a) => {
          if (a.action !== 'input-change') return;
          if (isSelectingRef.current) return;
          setLocation({ location: l, locationUrl: undefined });
        },
        onChange: (value) => {
          isSelectingRef.current = true;
          handleClick(value?.label, value?.value.place_id);
          window.setTimeout(() => {
            isSelectingRef.current = false;
          }, 0);
        },
        className: 'w-full min-w-0',
        components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
        placeholder: location || props.placeholder,
        styles: {
          control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused ? COLOR_PRIMARY_300 : `${COLOR_SECONDARY_200}80`,
            borderRadius: '15px',
            borderWidth: '2px',
            color: COLOR_PRIMARY_900,
            backgroundColor: `${COLOR_SECONDARY_200}80`,
            boxShadow: 'none',
            height: '50px',
            fontWeight: '500',
            fontSize: '16px',
            '&:hover': {
              borderColor: state.isFocused ? COLOR_PRIMARY_300 : `${COLOR_SECONDARY_200}80`,
            },
          }),
          input: (baseStyles) => ({
            ...baseStyles,
            color: COLOR_PRIMARY_900,
            cursor: 'text',
          }),
          option: (baseStyles, { isSelected }) => ({
            ...baseStyles,
            backgroundColor: isSelected ? COLOR_PRIMARY : COLOR_SECONDARY_50,
            color: isSelected ? COLOR_SECONDARY_50 : COLOR_PRIMARY_900,
            '&:hover': {
              backgroundColor: isSelected ? COLOR_PRIMARY_300 : COLOR_PRIMARY_300,
              color: COLOR_PRIMARY_900,
            },
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          dropdownIndicator: (baseStyles, state) => ({
            ...baseStyles,
            color: state.isFocused ? COLOR_PRIMARY_300 : COLOR_PRIMARY,
          }),
        },
      }}
      {...props}
    />
  );
}
